import React, { useState } from 'react';
import Button from '../UI/Button';
import Card from '../UI/Card';
import CreateOfferModal from './CreateOfferModal';
import { useUserContext } from '../../contexts/UserContext';
import { createContentfulEntry, createTenantActivityOffers, editExperienceOfferMapping, getTenantActivityOffers, updateAEMContentFragments, updateTenantActivityOffers } from '../../utils/APIs';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';
import Loader from '../UI/Loader/Loader';
import { isValidJsonString } from '../../utils/helper';
import { toast } from 'react-toastify';
import ConfirmModal from '../UI/Modal/confirmModal';
import UpdateOfferModal from './UpdateOfferModal';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import CreateContentfulModal from './CreateContentfulModal';
import UpdateContentfulModal from './UpdateContentfulModal';
import CreateTealiumModal from './CreateTealiumModal';
import { Sparkles, RefreshCw, Layers, Database, Zap, AlertCircle } from 'lucide-react';

const Activation = ({
    setOfferId,
    experience,
    setExperience,
    validationError,
    setValidationError,
    handleValidateJson,
    offerPermission,
    selectedTemplate,
    code,
    aemPermission,
    contentfulPermission,
    offerActionConfig,
    tealiumPermission,
}) => {
    const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
    const [showUpdateOfferModal, setShowUpdateOfferModal] = useState(false);
    const [offerSuccessMessage, setOfferSuccessMessage] = useState(false);
    const [openUpdateAEMModal, setOpenUpdateAEMModal] = useState(false)
    const [contentfulModal, setContentfulModal] = useState({action:null})
    const [tealiumModalAction, setTealiumModalAction] = useState({action:null})
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { enums, content } = useContentfulContext();
    const { userData, checkPermission } = useUserContext();
    const selectedOrgLevel = userData?.selectedOrganization?.level;

    const hideUpdateCta = !!offerActionConfig && ["aem", "contentful"].includes(offerActionConfig.source);
    const handleCheckValidation = () => {
        // code may be a pre-parsed object (from ContentfulEntryDetails) or a JSON string (from editor)
        const isValidJson = selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id
            ? true
            : (typeof code === 'object' && code !== null) || isValidJsonString(code);
        if (!isValidJson) {
            setValidationError(content.EDIT_EXPERIENCE.invalid_json);
            return;
        }
        const isValidate = handleValidateJson();
        if (isValidate.length > 0) {
            setError(isValidate.join(", \n"))
            return;
        }
        setError("");
        return true;
    }

    const createOfferSubmitHandler = async (values, actions) => {
        try {
            let result;
            // check for offerActionConfig 
            if (offerActionConfig && offerActionConfig?.handler) {
                const handleCreateOffer = offerActionConfig?.handler
                setLoading(true);
                result = await handleCreateOffer(values)
            } else {
                if (!userData.defaultAdobeIO?.tenant_name) {
                    toast.error(content.EXPERIENCE_CODE_CONTENT.choose_one_adobe_io);
                    return;
                }

                const isValidate = selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id ? [] : handleValidateJson();
                if (isValidate.length > 0) {
                    setError(isValidate.join(", \n"));
                    return;
                }

                setLoading(true);
                result = await createTenantActivityOffers(
                    userData?.token,
                    userData?.selectedOrganization?.org_token,
                    {
                        tenant: userData.defaultAdobeIO?.tenant_name,
                        data: {
                            name: values.offer_name,
                            workspace: userData.defaultWorkspace?.workspace_id,
                            content: selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id
                                ? code
                                : (typeof code === 'object' && code !== null ? code : JSON.parse(code)),
                            type: selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id ? 'content' : 'json',
                        },
                        experience_id: experience?._id,
                    },
                    {
                        event_name: ANALYTICS_EVENTS.ACTIVATE_EXPERIENCE,
                        metadata: { experience_id: experience?._id, experience_name: experience?.experience_name, offer_name: values.offer_name, destination: 'adobe_target', tenant: userData.defaultAdobeIO?.tenant_name, activated: true }
                    }
                );
                // only update in case of experience flow 
                setExperience(exp => {
                    const associatedOfferIds = exp?.associated_adobe_target_offer_id || [];
                    return { ...exp, associated_adobe_target_offer_id: [...associatedOfferIds, result.data.id] };
                })
                setOfferId(result.data.id);
            }
            if (result.data && experience?._id) {
                const offerMappingPayload = {
                    experience_id: experience._id,
                    id: result.data.id,
                    integration_id: userData?.defaultAdobeIO?._id,
                    destination: "adobe_target"
                };
                await editExperienceOfferMapping(userData?.token, userData?.selectedOrganization?.org_token, offerMappingPayload);
            }
            setError("");
            setOfferSuccessMessage(`Offer is created in Adobe Target. Offer Id - ${result.data.id}, Workspace Name: ${userData.defaultWorkspace?.workspace_name}`);
        } catch (error) {
            setError(error?.message || content.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const updateOfferSubmitHandler = async (selectedOfferIds) => {
        try {
            setLoading(true);
            const offerDataList = await getTenantActivityOffers(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                {
                    ids: selectedOfferIds,
                    type: selectedTemplate.type === enums?.templateTypes.json_a_to_html.id ? 'content' : 'json',
                    tenant: userData.defaultAdobeIO?.tenant_name
                }
            );
            const updatePayload = {
                ids: selectedOfferIds,
                tenant: userData.defaultAdobeIO?.tenant_name,
                type: selectedTemplate.type === enums?.templateTypes.json_a_to_html.id ? 'content' : 'json',
                data: offerDataList.data.map((offerData) => ({
                    id: offerData.id,
                    name: offerData.name,
                    content: selectedTemplate.type === enums?.templateTypes.json_a_to_html.id
                                ? code
                                : (typeof code === 'object' && code !== null ? code : JSON.parse(code)),
                })),
            };
    
            const response = await updateTenantActivityOffers(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                updatePayload,
                {
                    event_name: ANALYTICS_EVENTS.ACTIVATE_EXPERIENCE,
                    metadata: {
                        experience_id: experience?._id,
                        experience_name: experience?.experience_name,
                        offer_ids: selectedOfferIds,
                        destination: 'adobe_target',
                        tenant: userData.defaultAdobeIO?.tenant_name,
                        activated: false
                    }
                }
            );
    
            setError("");
            setShowUpdateOfferModal(false);
            // Handle success/failure messages
            if (response.data.successCount === selectedOfferIds.length) {
                console.log(content.EDIT_EXPERIENCE.all_offers_update_success)
                toast.success(`${response.data.successCount} offers updated successfully.`);
            } else if (response.data.failedCount > 0) {
                const failedIds = response.data.failedUpdates.map(f => f.id).join(', ');
                
                if (response.data.successCount > 0) {
                    // Partial success
                    toast.warning(
                        content.EDIT_EXPERIENCE.partial_offers_update_success
                            .replace('${successCount}', response.data.successCount)
                            .replace('${failedCount}', response.data.failedCount)
                    );
                } else {
                    // All failed
                    toast.error(
                        content.EDIT_EXPERIENCE.offers_update_failed
                            .replace('${failedCount}', response.data.failedCount)
                    );
                }
    
                // Show failed IDs if any
                if (failedIds.length > 0) {
                    toast.error(
                        content.EDIT_EXPERIENCE.failed_offer_ids
                            .replace('${failedIds}', failedIds),
                        { autoClose: 5000 }
                    );
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message 
                || error.message 
                || content.COMMON_MESSAGE.something_wrong_msg;
            
            setError(errorMessage);
            
            // Show error toast only if not a structured response
            if (!error.response?.data?.failedCount) {
                toast.error(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const addContentfulEntry = async (contentType)=>{
        try {
            if(!offerActionConfig && !offerActionConfig?.source){
                const isValidate = selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id ? [] : handleValidateJson();
                if (isValidate.length > 0) {
                    setError(isValidate.join(", \n"));
                    return;
                }
            }

            const locale = "en-US";
            const codeValue = selectedTemplate?.type === enums?.templateTypes.json_a_to_html.id
                ? { content: code }
                : (typeof code === "string" && isValidJsonString(code) ? JSON.parse(code) : code);

            let fields;
            if (contentType.isSingleField && contentType.fieldKey) {
                // Single-field JSON content type: wrap entire content under the one field key
                fields = {
                    [contentType.fieldKey]: {
                        [locale]: codeValue
                    }
                };
            } else {
                // Multi-field content type: each key in the content object maps to its own field
                const contentObj = (typeof codeValue === 'object' && codeValue !== null) ? codeValue : {};
                fields = {};
                for (const key of Object.keys(contentObj)) {
                    fields[key] = { [locale]: contentObj[key] };
                }
            }

            const updatedEntry = { fields };

            setLoading(true);
            const payload = {
                token: userData?.token,
                content_type: contentType.id,
                organizationId: userData?.selectedOrganization?.org_token,
                fieldsData : updatedEntry,
                contentful_id: userData?.defaultContentful?._id,
                experience_id : experience?._id
            }
            const result = await createContentfulEntry(payload)
            // for this experience offer mapping created in backend 
            if (result.status) {
                toast.success(result.message)
                setContentfulModal({ action: null })
                setExperience(exp => {
                    const associatedOfferIds = exp?.associated_contentful_offer_id || [];
                    return { ...exp, associated_contentful_offer_id: [...associatedOfferIds, result.data?.sys?.id] };
                })
            }
        } catch (error) {
            setError(error?.message || content.COMMON_MESSAGE.something_wrong_msg);
        } finally{
            setLoading(false)
        }
    }

    const updateContentfulEntry = async ( selectedEntryIds ) => {}

    const handleOpenUpdateAEMModal = () => {
        setOpenUpdateAEMModal(true)
    }
    const handleCloseUpdateAEMModal = () => {
        setOpenUpdateAEMModal(false)
    }

    const handleUpdateAEM = async () => {
        try {
            if (selectedTemplate.type != enums?.templateTypes.no_preview.id) return;
            setLoading(true);
            if (!handleCheckValidation()) return;
            const payload = {
                aem_adobe_io_id: userData?.defaultAEMAdobeIO?._id,
                template: selectedTemplate?._id,
                scope: selectedTemplate?.scope,
                value: {
                    properties: {
                        elements: {
                            displayConfig: {
                                value: code
                            }
                        }
                    }
                }
            };
            const result = await updateAEMContentFragments(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                payload
            );
            setError('');
            toast.success(result.message)
            setOpenUpdateAEMModal(false)
        } catch (error) {
            setError(error?.message || content.COMMON_MESSAGE.something_wrong_msg)
        } finally {
            setLoading(false);
        }
    }

    const hideCreateOfferModal = () => { 
        setShowCreateOfferModal(false);
        setOfferSuccessMessage(false);
    };

    return (
        <div>
            {loading && <Loader />}
            <div className="max-w-4xl mx-auto space-y-6">
                {validationError && (
                    <div className="flex items-start gap-3 p-4 bg-stride-danger/10 border border-stride-danger/30 text-stride-danger rounded-xl" role="alert">
                        <AlertCircle size={18} className="shrink-0 mt-0.5" />
                        <p className="text-sm font-medium">{validationError}</p>
                    </div>
                )}

                {selectedOrgLevel?.route_permission['adobe-io-integrations'] == true && (
                    <Card padding="none" className="overflow-hidden">
                        <div className="px-6 py-4 border-b border-stride-border bg-stride-bg flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-stride-primary/10 text-stride-primary flex items-center justify-center">
                                    <Sparkles size={16} />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-stride-heading">
                                        {content.EXPERIENCE_CODE_CONTENT.adobe_target.title || "Adobe Target"}
                                    </h2>
                                    <p className="text-xs text-stride-muted">
                                        Sync and publish generated offers directly to Adobe Target workspace.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="flex flex-wrap items-center gap-3">
                                {checkPermission(offerPermission.create) && (
                                    <Button
                                        variant="primary"
                                        icon={<Sparkles size={15} />}
                                        onClick={() => setShowCreateOfferModal(true)}
                                    >
                                        {content.EXPERIENCE_CODE_CONTENT.create_offer_btn}
                                    </Button>
                                )}
                                {checkPermission(offerPermission.edit) && !hideUpdateCta && (experience?.associated_adobe_target_offer_id && experience?.associated_adobe_target_offer_id.length > 0) && (
                                    <Button
                                        variant="secondary"
                                        icon={<RefreshCw size={15} />}
                                        onClick={() => setShowUpdateOfferModal(true)}
                                    >
                                        {content.EXPERIENCE_CODE_CONTENT.update_offer_btn}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Card>
                )}

                {selectedOrgLevel?.route_permission['aem-adobe-io'] == true &&
                    selectedTemplate?.type == enums?.templateTypes.no_preview.id && 
                    selectedTemplate.source == enums?.templateSources.api.id && (
                        <Card padding="none" className="overflow-hidden">
                            <div className="px-6 py-4 border-b border-stride-border bg-stride-bg flex items-center justify-between">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                        <Layers size={16} />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-semibold text-stride-heading">
                                            {content.EXPERIENCE_CODE_CONTENT.aem.title || "Adobe Experience Manager (AEM)"}
                                        </h2>
                                        <p className="text-xs text-stride-muted">
                                            Publish experience code to AEM Content Fragments.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6">
                                {checkPermission(aemPermission) && (
                                    <Button
                                        variant="primary"
                                        icon={<RefreshCw size={15} />}
                                        onClick={handleOpenUpdateAEMModal}
                                    >
                                        {content.EXPERIENCE_CODE_CONTENT.update_aem.btn}
                                    </Button>
                                )}
                            </div>
                        </Card>
                    )
                }

                {selectedOrgLevel?.route_permission['tealium-api-keys'] == true && (
                    <Card padding="none" className="overflow-hidden">
                        <div className="px-6 py-4 border-b border-stride-border bg-stride-bg flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center">
                                    <Zap size={16} />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-stride-heading">
                                        {content.EXPERIENCE_CODE_CONTENT.tealium.title || "Tealium"}
                                    </h2>
                                    <p className="text-xs text-stride-muted">
                                        Deploy audience attributes and activation payloads to Tealium.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            {checkPermission(tealiumPermission?.create) && (
                                <Button
                                    variant="primary"
                                    icon={<Zap size={15} />}
                                    onClick={() => setTealiumModalAction({ action: "create" })}
                                >
                                    {content.EXPERIENCE_CODE_CONTENT.tealium.create_btn}
                                </Button>
                            )}
                        </div>
                    </Card>
                )}

                {selectedOrgLevel?.route_permission['contentful-entries'] == true && (
                    <Card padding="none" className="overflow-hidden">
                        <div className="px-6 py-4 border-b border-stride-border bg-stride-bg flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                    <Database size={16} />
                                </div>
                                <div>
                                    <h2 className="text-base font-semibold text-stride-heading">
                                        {content.EXPERIENCE_CODE_CONTENT.contentful.title || "Contentful"}
                                    </h2>
                                    <p className="text-xs text-stride-muted">
                                        Create structured entry records in target Contentful space.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6">
                            {checkPermission(contentfulPermission.create) && (
                                <Button
                                    variant="primary"
                                    icon={<Database size={15} />}
                                    onClick={() => setContentfulModal({ action: "create" })}
                                >
                                    {content.EXPERIENCE_CODE_CONTENT.contentful.create_btn}
                                </Button>
                            )}
                        </div>
                    </Card>
                )}

                {selectedOrgLevel?.route_permission['adobe-io-integrations'] == false &&
                    selectedOrgLevel?.route_permission['aem-adobe-io'] == false &&
                    selectedOrgLevel?.route_permission['contentful-entries'] == false && (
                        <div className="flex items-start gap-3 p-4 bg-stride-danger/10 border border-stride-danger/30 text-stride-danger rounded-xl w-full" role="alert">
                            <AlertCircle size={18} className="shrink-0 mt-0.5" />
                            <p className="text-sm font-medium">{content.EXPERIENCE_CODE_CONTENT.no_activity_found}</p>
                        </div>
                    )
                }
            </div>

            {showCreateOfferModal && (
                <CreateOfferModal
                    hideCreateOfferModal={hideCreateOfferModal}
                    offerSuccessMessage={offerSuccessMessage}
                    loading={loading}
                    handleSubmit={createOfferSubmitHandler}
                    error={error}
                />
            )}

            {showUpdateOfferModal && !hideUpdateCta && (
                <UpdateOfferModal
                    setShowUpdateOfferModal={setShowUpdateOfferModal}
                    loading={loading}
                    handleSubmit={updateOfferSubmitHandler}
                    error={error}
                    experience={experience}
                />
            )}

            {contentfulModal.action === "create" && (
                <CreateContentfulModal
                    closeModal={()=>{
                        setContentfulModal({action : null})
                        setError("")
                    }}
                    loading={loading}
                    handleSubmit={addContentfulEntry}
                    error={error}
                    selectedTemplate={selectedTemplate}
                    experienceJson={typeof code === 'object' && code !== null ? code : (typeof code === 'string' && isValidJsonString(code) ? JSON.parse(code) : null)}
                />
            )}
            {contentfulModal.action === "edit" && (
                <UpdateContentfulModal
                    closeModal={()=>{
                        setContentfulModal({action : null})
                        setError("")
                    }}
                    loading={loading}
                    handleSubmit={updateContentfulEntry}
                    error={error}
                />
            )}

            {tealiumModalAction.action === "create" && (
                <CreateTealiumModal
                    closeModal={()=>{
                        setTealiumModalAction({action : null})
                        setError("")
                    }}
                    experience_id={experience?._id}
                    code={code}
                />
            )}

            {openUpdateAEMModal && (
                <ConfirmModal
                    title={content.EXPERIENCE_CODE_CONTENT.update_aem.modal_title}
                    error={error}
                    description={content.EXPERIENCE_CODE_CONTENT.update_aem.modal_description}
                    hideModal={handleCloseUpdateAEMModal}
                    handleConfirmation={handleUpdateAEM}
                />
            )}

        </div>
    );
};

export default Activation;